<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';
import FileUpload from 'vue-upload-component';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { DirectUpload } from 'activestorage';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const attachments = ref([]);
const isRecordingAudio = ref(false);
const recordingDuration = ref('00:00');
const isUploadingFile = ref(false);
const audioRecorderRef = ref(null);

const hasAttachments = computed(() => attachments.value.length > 0);
const showSendButton = computed(
  () => newMessage.value.length > 0 || hasAttachments.value
);

const formatMessagesForApi = () => {
  return messages.value.map(message => ({
    role: message.sender,
    content: message.content,
  }));
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
  attachments.value = [];
};

// Watch for assistant ID changes and reset conversation
watch(
  () => assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      resetConversation();
    }
  }
);

const handleAttachmentUpload = attachment => {
  attachments.value.push(attachment);
  isUploadingFile.value = false;
};

const removeAttachment = index => {
  attachments.value.splice(index, 1);
};

const startVoiceRecording = () => {
  isRecordingAudio.value = true;
};

const stopVoiceRecording = () => {
  if (audioRecorderRef.value) {
    audioRecorderRef.value.stopRecording();
  }
};

const onRecorderProgressChanged = duration => {
  recordingDuration.value = duration;
};

const onFinishVoiceRecord = audioFile => {
  isRecordingAudio.value = false;
  recordingDuration.value = '00:00';
  // Stage audio as attachment
  handleAttachmentUpload({
    file: audioFile.file,
    thumbUrl: null,
    fileType: 'audio',
    name: audioFile.name,
  });
};

const onFileUpload = async file => {
  if (!file) return;

  isUploadingFile.value = true;
  try {
    if (checkFileSizeLimit(file, 40 * 1024 * 1024)) {
      const upload = new DirectUpload(
        file.file,
        '/rails/active_storage/direct_uploads'
      );

      upload.create((error, blob) => {
        if (error) {
          // eslint-disable-next-line no-console
          console.error('Upload error:', error);
        } else {
          handleAttachmentUpload({
            file: blob.signed_id,
            thumbUrl: window.URL.createObjectURL(file.file),
            fileType: file.type.includes('image') ? 'image' : 'file',
            name: file.name,
          });
        }
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('File upload error:', error);
  } finally {
    isUploadingFile.value = false;
  }
};

const sendMessage = async () => {
  if ((!newMessage.value.trim() && !hasAttachments.value) || isLoading.value)
    return;

  const userMessage = {
    content: newMessage.value || '(Attachment)',
    sender: 'user',
    timestamp: new Date().toISOString(),
    attachments: [...attachments.value],
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';
  attachments.value = [];

  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
    });

    messages.value.push({
      content: data.response,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-lucide-rotate-ccw"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <div class="mx-6">
      <!-- Attachment Previews -->
      <div
        v-if="hasAttachments"
        class="flex flex-wrap gap-2 mb-2 p-2 bg-n-solid-1 rounded-lg"
      >
        <div
          v-for="(attachment, index) in attachments"
          :key="index"
          class="relative group"
        >
          <img
            v-if="attachment.fileType === 'image'"
            :src="attachment.thumbUrl"
            class="h-16 w-16 object-cover rounded-md border border-n-weak"
          />
          <div
            v-else
            class="h-16 w-16 flex items-center justify-center bg-n-solid-2 rounded-md border border-n-weak text-xs text-center p-1"
          >
            <span class="truncate">{{ attachment.name || 'Audio' }}</span>
          </div>
          <button
            class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
            @click="removeAttachment(index)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </div>
      </div>

      <!-- Voice Recording UI -->
      <div
        v-if="isRecordingAudio"
        class="flex items-center gap-3 p-3 bg-n-background rounded-lg border border-red-500 mb-2"
      >
        <AudioRecorder
          ref="audioRecorderRef"
          audio-record-format="audio/mp3"
          class="flex-1"
          @recorder-progress-changed="onRecorderProgressChanged"
          @finish-record="onFinishVoiceRecord"
        />
        <span class="text-sm text-red-500 font-medium">{{
          recordingDuration
        }}</span>
        <NextButton
          ghost
          sm
          danger
          icon="i-lucide-x"
          @click="stopVoiceRecording"
        />
      </div>

      <!-- Input Area -->
      <div
        v-if="!isRecordingAudio"
        class="flex items-center bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
      >
        <FileUpload
          :size="40 * 1024 * 1024"
          accept="image/*,audio/*,video/*,.pdf,.doc,.docx"
          @input-file="onFileUpload"
        >
          <NextButton
            ghost
            sm
            slate
            icon="i-lucide-paperclip"
            :loading="isUploadingFile"
          />
        </FileUpload>
        <button
          v-if="!newMessage.trim() && !hasAttachments"
          class="flex items-center justify-center h-8 w-8 text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="startVoiceRecording"
        >
          <span class="i-lucide-mic w-5 h-5" />
        </button>
        <input
          v-model="newMessage"
          class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
          :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
          @keyup.enter="sendMessage"
        />
        <NextButton
          ghost
          sm
          :disabled="!showSendButton"
          icon="i-lucide-send"
          @click="sendMessage"
        />
      </div>
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
